import {
  List,
  Edit,
  Datagrid,
  SimpleForm,
  TextField,
  TextInput,
  BooleanField,
  BooleanInput,
  NumberInput,
  Toolbar,
  SaveButton,
  useRecordContext,
} from 'react-admin';
import { useForm } from 'react-final-form';
import { useEffect, useState } from 'react';

import Pagination from 'components/Pagination';
import { defaultPerPage } from 'utils/helpers';

const stringifyValue = value => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
};

// Keys whose value MUST be a specific shape — a malformed entry here (e.g. an
// array typed with a missing bracket) would otherwise be saved as a bare
// string and break payment routing / retry scheduling on the backend. We
// reject those at edit time instead of silently degrading.
const EXPECTED_TYPES = {
  deferred_enabled: 'boolean',
  block_buyer_on_failed_charge: 'boolean',
  charge_max_attempts: 'number',
  deferred_payment_methods: 'array',
  charge_retry_schedule_hours: 'array',
};

const typeOfParsed = value => (Array.isArray(value) ? 'array' : typeof value);

const parseValueInput = raw => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const trimmed = String(raw).trim();

  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;

  if (/^-?\d+$/.test(trimmed)) return parseInt(trimmed, 10);
  if (/^-?\d*\.\d+$/.test(trimmed)) return parseFloat(trimmed);

  // If the input *looks* like JSON (array/object/quoted string), it MUST parse.
  // We deliberately let JSON.parse throw here so the caller can surface a real
  // "Invalid JSON" error and block the save, rather than silently coercing a
  // malformed value to a bare string.
  if (
    (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('"') && trimmed.endsWith('"'))
  ) {
    return JSON.parse(trimmed);
  }

  return trimmed;
};

const ValueCell = ({ source }) => {
  const record = useRecordContext();
  return <span style={{ fontFamily: 'monospace' }}>{stringifyValue(record?.[source])}</span>;
};

export const PlatformSettingList = props => (
  <List
    {...props}
    perPage={defaultPerPage}
    pagination={<Pagination />}
    bulkActionButtons={false}
    sort={{ field: 'key', order: 'ASC' }}
    exporter={false}
  >
    <Datagrid rowClick="edit">
      <TextField source="key" />
      <ValueCell source="value" label="Value" />
      <ValueCell source="default" label="Default" />
      <BooleanField source="overridden" />
    </Datagrid>
  </List>
);

// Custom input that handles JSON-ish parsing on submit. Edits the in-form
// `value` field as a string while preserving the parsed value at submit time
// by updating the final-form state.
const ValueJsonInput = () => {
  const record = useRecordContext();
  const form = useForm();
  const [text, setText] = useState(() => stringifyValue(record?.value));
  const [error, setError] = useState(null);

  useEffect(() => {
    setText(stringifyValue(record?.value));
  }, [record?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate on every change so the form value stays correct, but only show
  // the error inline — no toast per keystroke (typing "false" passes through
  // "f", "fa", … which are all invalid mid-word).
  const handleChange = e => {
    const next = e.target.value;
    setText(next);
    try {
      const parsed = parseValueInput(next);
      const expected = EXPECTED_TYPES[record?.key];
      if (expected && typeOfParsed(parsed) !== expected) {
        setError(`"${record.key}" must be ${expected === 'array' ? 'an' : 'a'} ${expected} (currently ${typeOfParsed(parsed)}).`);
        return; // don't push an invalid value into the form
      }
      form.change('value', parsed);
      setError(null);
    } catch (err) {
      // parseValueInput throws on malformed JSON — surface it inline and leave
      // the form value untouched so the bad input can't be saved.
      setError(`Invalid JSON value${err?.message ? `: ${err.message}` : ''}`);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Value</label>
      <textarea
        value={text}
        onChange={handleChange}
        rows={4}
        style={{
          width: '100%',
          fontFamily: 'monospace',
          padding: 8,
          border: `1px solid ${error ? '#f44336' : '#ccc'}`,
          borderRadius: 4,
          fontSize: 14,
        }}
      />
      <small style={{ color: '#666' }}>
        JSON arrays/objects, numbers, and quoted strings are parsed automatically. Bare text is
        sent as a string.
      </small>
      {error && (
        <div style={{ color: '#f44336', marginTop: 4, fontSize: 13 }}>{error}</div>
      )}
    </div>
  );
};

// Legacy/bad rows may hold the STRING "true"/"false" instead of a boolean —
// render those as their boolean meaning so the toggle reflects reality, and
// always submit a real boolean.
const asBoolean = value => value === true || value === 'true';

// Pick the right input for the setting's type: a switch for booleans, a number
// field for numbers, and the JSON textarea only for arrays/objects/strings.
// Type comes from EXPECTED_TYPES (authoritative) or the default's own type.
const TypedValueInput = () => {
  const record = useRecordContext();
  const expected = EXPECTED_TYPES[record?.key] || typeOfParsed(record?.default);

  if (expected === 'boolean') {
    return (
      <BooleanInput
        source="value"
        label={`${record?.key} enabled`}
        format={asBoolean}
        parse={v => !!v}
      />
    );
  }

  if (expected === 'number') {
    return (
      <NumberInput
        source="value"
        label="Value"
        parse={v => (v === '' || v === null || v === undefined ? null : Number(v))}
      />
    );
  }

  return <ValueJsonInput />;
};

// Settings rows can't be deleted (backend has no destroy route) — save only.
const SettingToolbar = props => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

export const PlatformSettingEdit = props => (
  <Edit {...props} mutationMode="pessimistic">
    <SimpleForm toolbar={<SettingToolbar />}>
      <TextInput source="key" disabled />
      <TextInput source="default" disabled format={stringifyValue} />
      <TypedValueInput />
    </SimpleForm>
  </Edit>
);
